// Page-specific styles for PhantomSlipHistoryPanel.
// Compact list rows tuned for Bloomberg-terminal data density per the
// design system §1 ("data density, monospace precision"). All values
// from --sz-* tokens; no hex literals; no icons; no emoji.
//
// Phase 9C.10c: local `statusPillBase / statusPillWon / statusPillBusted /
// statusPillExpired` exports removed — consumers now use shared
// `pillSuccessOutline / pillDangerOutline / pillNeutral` from
// `src/styles/status-pills.ts`. Card elevation + transition spread
// via the new sibling `phantom-slip-history-panel-rails.styles.ts`.

import type { CSSProperties } from "react";

export const list: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-3)",
};

const rowBase: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "14px 16px",
  background: "var(--sz-bg-surface-2)",
  border: "1px solid var(--sz-border-subtle)",
  borderRadius: "var(--sz-radius-md)",
};

// Left-border accent communicates the row's terminal status colour:
// green for won, warning for busted, neutral for expired. Tokens only.
export const rowWon: CSSProperties = {
  ...rowBase,
  borderLeft: "2px solid var(--sz-success)",
};

export const rowBusted: CSSProperties = {
  ...rowBase,
  borderLeft: "2px solid var(--sz-warning)",
};

export const rowExpired: CSSProperties = {
  ...rowBase,
  borderLeft: "2px solid var(--sz-border-default)",
};

export const rowHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  flexWrap: "wrap",
  gap: "var(--sz-space-3)",
};

export const resolvedAt: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
};

export const metaGrid: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--sz-space-3) var(--sz-space-5)",
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
};

export const metaItem: CSSProperties = {
  display: "inline-flex",
  alignItems: "baseline",
  gap: "6px",
};

export const metaKey: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const metaValue: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "0.5px",
  color: "var(--sz-text-primary)",
};

export const bustLine: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "0.5px",
  color: "var(--sz-text-muted)",
};

export const safetyLine: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-muted)",
  lineHeight: 1.55,
};

export const errorBar: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-danger-text)",
  background: "var(--sz-danger-dim)",
  border: "1px solid var(--sz-danger)",
  padding: "10px 14px",
  borderRadius: "var(--sz-radius-md)",
};
